'use client'

import { z } from 'zod'

import { AppTrans } from '@mntn-dev/app-common'
import { UrlNormalizerSchema } from '@mntn-dev/app-form-schemas'
import { useCookieParams } from '@mntn-dev/app-navigation/client'
import { mapRegistrationTypeToOrganizationType } from '@mntn-dev/app-routing'
import {
  OrganizationSignUpModelSchema,
  type SignUpModel,
  SignUpModelSchema,
  UserSignUpModelSchema,
} from '@mntn-dev/domain-types'
import { Controller, useForm, zodResolver } from '@mntn-dev/forms'
import { NarrowTFunction, useTranslation } from '@mntn-dev/i18n'
import { Button } from '@mntn-dev/ui-components/buttons'
import { Checkbox } from '@mntn-dev/ui-components/checkbox'
import { Form } from '@mntn-dev/ui-components/form'
import { FormField } from '@mntn-dev/ui-components/form-field'
import { Heading } from '@mntn-dev/ui-components/heading'
import { Input } from '@mntn-dev/ui-components/input'
import { Link } from '@mntn-dev/ui-components/link'
import { RadioGroup } from '@mntn-dev/ui-components/radio-group'
import { Stack } from '@mntn-dev/ui-components/stack'
import { Text } from '@mntn-dev/ui-components/text'
import { normalizeUrlString } from '@mntn-dev/utilities'

import { useUnauthenticatedSession } from '~/hooks/secure/use-unauthenticated-session.ts'
import {
  UniqueOrganizationNameSchemaBuilder,
  UniqueUserEmailAddressSchemaBuilder,
} from '~/schemas/public.schema.ts'

import { useSignUpAgreement } from '../hooks/use-sign-up-agreement.ts'

type SignUpFormProps = {
  isSaving: boolean
  onSubmit: (registration: SignUpModel) => Promise<void>
}

export const SignUpForm = ({
  isSaving,
  onSubmit: onSubmitProp,
}: SignUpFormProps) => {
  const { t } = useTranslation(['sign-up', 'validation'])
  const { t: tValidation } = useTranslation('validation')
  const { authn } = useUnauthenticatedSession()

  const { __type = 'video-professional' } = useCookieParams()
  const organizationType = mapRegistrationTypeToOrganizationType[__type]

  const onSubmit = async (registration: SignUpModel) => {
    const url = registration.organization.websiteUrl

    await onSubmitProp({
      ...registration,
      organization: {
        ...registration.organization,
        websiteUrl:
          typeof url === 'string' ? normalizeUrlString(url) : undefined,
      },
    })
  }

  const methods = useForm({
    reValidateMode: 'onSubmit', // The default onChange is a bad experience because the latency involved with server-side validation.
    defaultValues: {
      user: {
        firstName: authn?.claims?.first_name,
        lastName: authn?.claims?.last_name,
        emailAddress: authn?.claims?.primary_email,
      },
      organization: {
        name: '',
        organizationType,
        websiteUrl: '',
      },
      agreementCheck: false,
    },
    resolver: zodResolver(
      SignUpModelSchema.extend({
        user: UserSignUpModelSchema.extend({
          emailAddress: UniqueUserEmailAddressSchemaBuilder({
            t: NarrowTFunction<['validation']>(t),
            field: t('sign-up:form.field.user.emailAddress'),
          }),
        }),
        organization: OrganizationSignUpModelSchema.extend({
          name: UniqueOrganizationNameSchemaBuilder({
            t: NarrowTFunction<['validation']>(t),
            field: t('sign-up:form.field.organization.name'),
          }),
          websiteUrl: UrlNormalizerSchema,
        }),
        agreementCheck: z.literal(true),
      }),
      tValidation,
      {},
      { mode: 'async' }
    ),
  })

  const {
    handleSubmit,
    formState: { errors, isValidating },
    register,
    control,
    watch,
  } = methods

  const watchedOrganizationType = watch('organization.organizationType')

  const agreement = useSignUpAgreement({
    organizationType: watchedOrganizationType,
  })

  const isLoading = isSaving || isValidating
  const isDisabled = isLoading

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      id="package-service-modal"
      className="h-full w-full max-w-3xl gap-8"
      gap="gap-8"
    >
      <Stack gap="4" direction="col">
        <Heading fontSize="2xl">{t('sign-up:form.title')}</Heading>
        <Text fontSize="sm" textColor="secondary">
          {t('sign-up:form.subtitle')}
        </Text>
      </Stack>

      <Stack className="text-left">
        <Controller
          name="organization.organizationType"
          control={control}
          render={({ field }) => (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <RadioGroup
                {...field}
                disabled={isDisabled}
                className="contents"
                label={t('sign-up:form.organizationType.label')}
              >
                <RadioGroup.Item
                  dataTestId="sign-up-organization-type-brand-radio"
                  dataTrackingId="sign-up-organization-type-brand-radio"
                  value="brand"
                  title={t('sign-up:form.organizationType.brand.title')}
                  subtitle={t('sign-up:form.organizationType.brand.subtitle')}
                />
                <RadioGroup.Item
                  dataTestId="sign-up-organization-type-agency-radio"
                  dataTrackingId="sign-up-organization-type-agency-radio"
                  value="agency"
                  title={t('sign-up:form.organizationType.agency.title')}
                  subtitle={t('sign-up:form.organizationType.agency.subtitle')}
                />
              </RadioGroup>
            </div>
          )}
        />
      </Stack>

      <Form.Layout className="text-left" rowGap="6">
        <FormField
          columnSpan={6}
          hasError={!!errors.organization?.name}
          className="w-full"
        >
          <FormField.Label>
            {t('sign-up:form.field.organization.name')}
          </FormField.Label>
          <FormField.Control>
            <Input
              {...register('organization.name')}
              dataTestId="sign-up-organization-name-input"
              dataTrackingId="sign-up-organization-name-input"
              disabled={isDisabled}
            />
          </FormField.Control>
          {!!errors.organization?.name && (
            <FormField.Error>
              {errors.organization.name.message}
            </FormField.Error>
          )}
        </FormField>
        <FormField
          columnSpan={3}
          hasError={!!errors.user?.firstName}
          className="w-full"
        >
          <FormField.Label>
            {t('sign-up:form.field.user.firstName')}
          </FormField.Label>
          <FormField.Control>
            <Input
              {...register('user.firstName')}
              dataTestId="sign-up-user-first-name-input"
              dataTrackingId="sign-up-user-first-name-input"
              disabled={isDisabled}
            />
          </FormField.Control>
          {!!errors.user?.firstName && (
            <FormField.Error>{errors.user.firstName.message}</FormField.Error>
          )}
        </FormField>
        <FormField
          columnSpan={3}
          hasError={!!errors.user?.lastName}
          className="w-full"
        >
          <FormField.Label>
            {t('sign-up:form.field.user.lastName')}
          </FormField.Label>
          <FormField.Control>
            <Input
              {...register('user.lastName')}
              dataTestId="sign-up-user-last-name-input"
              dataTrackingId="sign-up-user-last-name-input"
              disabled={isDisabled}
            />
          </FormField.Control>
          {errors.user?.lastName && (
            <FormField.Error>{errors.user.lastName.message}</FormField.Error>
          )}
        </FormField>
        <FormField
          columnSpan={3}
          hasError={!!errors.organization?.websiteUrl}
          className="w-full"
        >
          <FormField.Label>
            {t('sign-up:form.field.organization.websiteUrl')}
          </FormField.Label>
          <FormField.Control>
            <Input
              {...register('organization.websiteUrl')}
              dataTestId="sign-up-organization-website-url-input"
              dataTrackingId="sign-up-organization-website-url-input"
              disabled={isDisabled}
            />
          </FormField.Control>
          {!!errors.organization?.websiteUrl && (
            <FormField.Error>
              {errors.organization.websiteUrl.message}
            </FormField.Error>
          )}
        </FormField>
        <FormField
          columnSpan={3}
          hasError={!!errors.user?.emailAddress}
          className="w-full"
        >
          <FormField.Label>
            {t('sign-up:form.field.user.emailAddress')}
          </FormField.Label>
          <FormField.Control>
            <Input
              {...register('user.emailAddress')}
              dataTestId="sign-up-user-email-address-input"
              dataTrackingId="sign-up-user-email-address-input"
              disabled={
                isDisabled || authn?.claims?.primary_email !== undefined
              }
            />
          </FormField.Control>
          {errors.user?.emailAddress && (
            <FormField.Error>
              {errors.user.emailAddress.message}
            </FormField.Error>
          )}
        </FormField>
        <FormField
          columnSpan={6}
          hasError={!!errors.agreementCheck}
          className="w-full items-center"
        >
          <div className="pt-2">
            <Controller
              name="agreementCheck"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  dataTestId="sign-up-terms-checkbox"
                  dataTrackingId="sign-up-terms-checkbox"
                  hasError={!!errors.agreementCheck}
                >
                  <Text fontSize="base" className="whitespace-nowrap">
                    <AppTrans
                      t={t}
                      i18nKey="sign-up:form.field.agreementCheck.label"
                      components={{
                        a: (
                          <Link
                            target="_blank"
                            className="underline hover:cursor-pointer text-link"
                            href={agreement.link}
                          />
                        ),
                      }}
                    />
                  </Text>
                </Checkbox>
              )}
            />
          </div>
        </FormField>
      </Form.Layout>
      <Stack className="pb-8 self-center">
        <Button
          dataTestId="sign-up-submit-button"
          dataTrackingId="sign-up-submit-button"
          type="submit"
          width="80"
          loading={isLoading}
          disabled={isDisabled}
        >
          {t('sign-up:form.action.submit')}
        </Button>
      </Stack>
    </Form>
  )
}
